import type { BookAppointmentResponse } from '@/features/scheduling/api/bookAppointment';
import { useGetStudentsSchedulingDetails, type GetStudentsSchedulingDetailsResponseData } from '@/features/scheduling/api/getStudentsSchedulingDetails';
import type { ApiErrorResponse } from '@/lib/apiClient';
import type { DateString, Id } from '@/types/api';
import type { EnrollmentType } from '@/types/enrollment';
import type { CenterSchedulingSettings, DeliveryType, RecurrencePattern, SessionType } from '@/types/scheduling';
import type { ReactNode } from 'react';
import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';

export type ScheduleStep = 'selectStudents' | 'noSessionsLeft' | 'sessionDetails' | 'dateAndTime' | 'summary' | 'success' | 'error';

export type SchedulingErrorType = 'fullyBooked' | 'generic';

export interface StudentsSessionDetails {
  studentId: Id;
  enrollmentId: Id;
  enrollmentType: EnrollmentType;
  enrollmentEndDate: string;
  deliveryType: DeliveryType;
  durationInMinutes: number;
  sessionType: SessionType;
  centerDetails: CenterSchedulingSettings;
}

export interface ScheduleFlowData {
  config: {
    isLoading: boolean;
    error: ApiErrorResponse | null;
    data?: GetStudentsSchedulingDetailsResponseData;
  };
  hasSessionDetailsSettings: boolean;
  selectedStudentsIds: Id[];
  studentSessionDetails?: StudentsSessionDetails[];
  dateAndTime: DateString;
  recurring: {
    enabled: boolean;
    recurrencePattern?: RecurrencePattern;
    endDate?: DateString;
  };
  bookedAppointmentsDetails?: BookAppointmentResponse;
}

export interface ScheduleFlowContextType {
  currentStep: ScheduleStep;
  data: ScheduleFlowData;
  goToStep: (step: ScheduleStep) => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  updateData: <K extends keyof ScheduleFlowData>(key: K, value: ScheduleFlowData[K]) => void;
  removeStudent: (studentId: Id) => void;
  resetFlow: () => void;
  errorType?: SchedulingErrorType;
  setErrorType: (type: SchedulingErrorType | undefined) => void;
}

const ScheduleFlowContext = createContext<ScheduleFlowContextType | undefined>(undefined);

export { ScheduleFlowContext };

const STEP_ORDER: ScheduleStep[] = ['selectStudents', 'sessionDetails', 'dateAndTime', 'summary', 'success'];

const getInitialData = (isLoading: boolean, error: ApiErrorResponse | null, data?: GetStudentsSchedulingDetailsResponseData): ScheduleFlowData => ({
  selectedStudentsIds: [],
  dateAndTime: '',
  recurring: {
    enabled: false,
    recurrencePattern: undefined,
    endDate: undefined,
  },
  config: {
    isLoading,
    error,
    data,
  },
  hasSessionDetailsSettings: true,
  bookedAppointmentsDetails: {
    notPossibleAppointments: [],
    notBookedAppointments: [],
    bookedAppointments: [],
  },
});

export const ScheduleFlowProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<ScheduleStep>('selectStudents');
  const [errorType, setErrorType] = useState<SchedulingErrorType | undefined>();

  const schedulingDetailsQuery = useGetStudentsSchedulingDetails({});

  const [data, setData] = useState<ScheduleFlowData>(() =>
    getInitialData(schedulingDetailsQuery.isLoading, schedulingDetailsQuery.error, schedulingDetailsQuery.data),
  );

  useEffect(() => {
    setData((prev) => ({
      ...prev,
      config: {
        isLoading: schedulingDetailsQuery.isLoading,
        error: schedulingDetailsQuery.error,
        data: schedulingDetailsQuery.data,
      },
    }));
  }, [schedulingDetailsQuery.isLoading, schedulingDetailsQuery.error, schedulingDetailsQuery.data]);

  const goToStep = useCallback((step: ScheduleStep) => {
    setCurrentStep(step);
  }, []);

  const goToNextStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex < STEP_ORDER.length - 1) {
      setCurrentStep(STEP_ORDER[currentIndex + 1]);
    }
  }, [currentStep]);

  const goToPreviousStep = useCallback(() => {
    const currentIndex = STEP_ORDER.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(STEP_ORDER[currentIndex - 1]);
    }
  }, [currentStep]);

  const updateData = useCallback(<K extends keyof ScheduleFlowData>(key: K, value: ScheduleFlowData[K]) => {
    setData((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const removeStudent = useCallback((studentId: Id) => {
    setData((prev) => ({
      ...prev,
      selectedStudentsIds: prev.selectedStudentsIds.filter((id) => id !== studentId),
      studentSessionDetails: prev.studentSessionDetails?.filter((details) => details.studentId !== studentId),
    }));
  }, []);

  const resetFlow = useCallback(() => {
    setCurrentStep('selectStudents');
    setErrorType(undefined);
    setData((prev) => ({
      selectedStudentsIds: [],
      dateAndTime: '',
      recurring: {
        enabled: false,
        recurrencePattern: undefined,
        endDate: undefined,
      },
      config: prev.config,
      hasSessionDetailsSettings: true,
    }));
  }, []);

  const contextValue = useMemo(
    () => ({
      currentStep,
      data,
      goToStep,
      goToNextStep,
      goToPreviousStep,
      updateData,
      removeStudent,
      resetFlow,
      errorType,
      setErrorType,
    }),
    [currentStep, data, goToStep, goToNextStep, goToPreviousStep, updateData, removeStudent, resetFlow, errorType],
  );

  return <ScheduleFlowContext value={contextValue}>{children}</ScheduleFlowContext>;
};
